// src/components/QuizGame.tsx
import React, { useState, useEffect } from 'react';

interface Question {
  id: number;
  question: string;
  answer: string;
  category: string;
}

const QuizGame: React.FC = () => {
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [showAnswer, setShowAnswer] = useState<boolean>(false);
  const [score, setScore] = useState<number>(0);
  const [quizStarted, setQuizStarted] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isAnimating, setIsAnimating] = useState<boolean>(false);

  const quizData: Question[] = [
    // Part 1: Questions About the Deen (Faith & Practice)
    { id: 1, question: "What is Islam?", answer: "Islam means submission to Allah's will by believing in His Oneness, obeying His commands, and avoiding His prohibitions.", category: "faith" },
    { id: 2, question: "What are the Five Pillars of Islam?", answer: "1. Shahadah (Testimony of Faith)\n2. Salah (Prayer)\n3. Zakah (Charity)\n4. Sawm (Fasting in Ramadan)\n5. Hajj (Pilgrimage to Makkah).", category: "faith" },
    { id: 3, question: "What are the Six Articles of Faith?", answer: "Belief in: 1. Allah\n2. Angels\n3. Divine Books\n4. Prophets\n5. Day of Judgment\n6. Divine Decree (Qadr).", category: "faith" },
    { id: 4, question: "What is Tawheed?", answer: "Tawheed is the belief that Allah is One and Unique — with no partners, no equals, and no offspring. It includes Oneness in Lordship, Worship, and Names & Attributes.", category: "faith" },
    { id: 5, question: "What invalidates one's Islam?", answer: "Associating partners with Allah (shirk), denying the Qur'an or Sunnah, mocking the religion, or rejecting any obligation clearly established in Islam.", category: "faith" },
    { id: 6, question: "What are the daily prayers (Salah)?", answer: "Fajr (2 rak'ahs), Dhuhr (4), Asr (4), Maghrib (3), and Isha (4). They are performed at fixed times throughout the day.", category: "faith" },
    { id: 7, question: "Why is Salah important?", answer: "It's the second pillar of Islam and the first thing a person will be judged for on the Day of Judgment. It connects the believer directly to Allah.", category: "faith" },
    { id: 8, question: "What is Zakah and who must give it?", answer: "It's an obligatory charity (2.5%) given from savings to purify wealth and help the poor.", category: "faith" },
    { id: 9, question: "What is Sawm (fasting)?", answer: "Abstaining from food, drink, and sin from dawn to sunset during Ramadan to gain piety.", category: "faith" },
    { id: 10, question: "What is Hajj and when is it performed?", answer: "The pilgrimage to Makkah once in a lifetime for those physically and financially able; performed during the month of Dhul-Hijjah.", category: "faith" },

    // Part 2: Questions About the Qur'an
    { id: 11, question: "What is the Qur'an?", answer: "The final and unchanged word of Allah, revealed to Prophet Muhammad ﷺ over 23 years through Angel Jibreel (Gabriel).", category: "quran" },
    { id: 12, question: "In which language was the Qur'an revealed?", answer: "Arabic.", category: "quran" },
    { id: 13, question: "How many Surahs are in the Qur'an?", answer: "114 Surahs (chapters).", category: "quran" },
    { id: 14, question: "Which Surah was revealed first?", answer: "Surah Al-'Alaq (96:1–5).", category: "quran" },
    { id: 15, question: "Which Surah was revealed last?", answer: "Surah An-Nasr (110).", category: "quran" },
    { id: 16, question: "What is the longest Surah?", answer: "Surah Al-Baqarah (Chapter 2).", category: "quran" },
    { id: 17, question: "What is the shortest Surah?", answer: "Surah Al-Kawthar (Chapter 108).", category: "quran" },
    { id: 18, question: "What is the central message of the Qur'an?", answer: "Worship Allah alone, live righteously, prepare for the Hereafter, and follow divine guidance.", category: "quran" },
    { id: 19, question: "What are Makki and Madani Surahs?", answer: "Makki Surahs were revealed before Hijrah (focus on belief), Madani after Hijrah (focus on laws and society).", category: "quran" },
    { id: 20, question: "How should Muslims treat the Qur'an?", answer: "With respect — recite it with purity, reflection, and understanding, and live by its teachings.", category: "quran" },

    // Part 3: Questions About Prophet Muhammad ﷺ
    { id: 21, question: "Who is Prophet Muhammad ﷺ?", answer: "The final messenger of Allah, born in Makkah in 570 CE, of the Quraysh tribe.", category: "prophet" },
    { id: 22, question: "What is his full name?", answer: "Muhammad ibn Abdullah ibn Abdul Muttalib ibn Hashim.", category: "prophet" },
    { id: 23, question: "Who were his parents?", answer: "His father was Abdullah, his mother was Amina bint Wahb.", category: "prophet" },
    { id: 24, question: "Who was his wet nurse?", answer: "Halimah Sa'diyyah.", category: "prophet" },
    { id: 25, question: "Who was his first wife?", answer: "Khadijah bint Khuwaylid (may Allah be pleased with her).", category: "prophet" },
    { id: 26, question: "When did he receive prophethood?", answer: "At the age of 40, in the cave of Hira.", category: "prophet" },
    { id: 27, question: "What was the first revelation?", answer: "'Read in the name of your Lord who created...' (Surah Al-'Alaq, 96:1).", category: "prophet" },
    { id: 28, question: "How long did his prophethood last?", answer: "23 years — 13 in Makkah, 10 in Madinah.", category: "prophet" },
    { id: 29, question: "What was his greatest miracle?", answer: "The Qur'an itself — preserved, unmatched, and eternal.", category: "prophet" },
    { id: 30, question: "When did the Prophet ﷺ pass away?", answer: "In 632 CE (11 AH) in Madinah, after completing his mission.", category: "prophet" },

    // Bonus Section: Questions Every Muslim Should Reflect On
    { id: 31, question: "Why were we created?", answer: "To worship Allah — 'I did not create jinn and humans except to worship Me.' (Qur'an 51:56)", category: "reflection" },
    { id: 32, question: "What happens after death?", answer: "Every soul will face judgment; believers enter Paradise, disbelievers face punishment.", category: "reflection" },
    { id: 33, question: "What is the difference between Sunnah and Hadith?", answer: "Sunnah is the way of the Prophet ﷺ, Hadith are recorded sayings and actions.", category: "reflection" },
    { id: 34, question: "What is Shirk?", answer: "Associating partners with Allah — the gravest sin that nullifies faith.", category: "reflection" },
    { id: 35, question: "What is the meaning of 'La ilaha illallah'?", answer: "'There is no god worthy of worship except Allah.' This is the foundation of Islam.", category: "reflection" }
  ];

  const filteredQuestions = selectedCategory === 'all' 
    ? quizData 
    : quizData.filter(q => q.category === selectedCategory);

  const startQuiz = () => {
    setQuizStarted(true);
    setCurrentQuestion(0);
    setShowAnswer(false);
    setScore(0);
  };

  const nextQuestion = () => {
    setIsAnimating(true);
    setTimeout(() => {
      if (currentQuestion < filteredQuestions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setShowAnswer(false);
      } else {
        setQuizStarted(false);
      }
      setIsAnimating(false);
    }, 300);
  };

  const revealAnswer = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setShowAnswer(true);
      setIsAnimating(false);
    }, 300);
  };

  const markCorrect = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setScore(score + 1);
      setShowAnswer(false);
      if (currentQuestion < filteredQuestions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        setQuizStarted(false);
      }
      setIsAnimating(false);
    }, 300);
  };

  const getCategoryName = (category: string) => {
    const categories: { [key: string]: string } = {
      'faith': '🕌 Faith & Practice',
      'quran': '📖 Quran Knowledge',
      'prophet': '🌙 Prophet Muhammad ﷺ',
      'reflection': '🌟 Reflection Questions',
      'all': '📚 All Categories'
    };
    return categories[category] || category;
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'faith': 'from-emerald-500 to-green-600',
      'quran': 'from-blue-500 to-cyan-600',
      'prophet': 'from-purple-500 to-indigo-600',
      'reflection': 'from-amber-500 to-orange-600',
      'all': 'from-gray-500 to-gray-700'
    };
    return colors[category] || 'from-gray-500 to-gray-700';
  };

  // Iman Verse Style Button Component
  const ImanButton = ({ 
    children, 
    onClick, 
    variant = 'primary',
    size = 'md',
    className = '',
    disabled = false,
    icon = null
  }: {
    children: React.ReactNode;
    onClick: () => void;
    variant?: 'primary' | 'success' | 'secondary' | 'accent' | 'outline';
    size?: 'sm' | 'md' | 'lg';
    className?: string;
    disabled?: boolean;
    icon?: string | null;
  }) => {
    const variantClasses = {
      primary: 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-blue-500/25',
      success: 'bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-green-500/25',
      secondary: 'bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white shadow-gray-500/25',
      accent: 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-amber-500/25',
      outline: 'bg-white dark:bg-gray-800 border-2 border-blue-500 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 shadow-blue-500/10'
    };

    const sizeClasses = {
      sm: 'px-4 py-2.5 text-sm rounded-lg',
      md: 'px-6 py-3.5 text-base rounded-xl',
      lg: 'px-8 py-4 text-lg rounded-2xl'
    };

    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={`
          relative
          ${sizeClasses[size]}
          ${variantClasses[variant]}
          font-semibold
          transition-all duration-300 ease-out
          transform
          hover:scale-105
          hover:shadow-xl
          active:scale-95
          shadow-lg
          border-b-4 border-r-2 border-white/30
          hover:border-b-2 hover:border-r-1
          active:border-b-0 active:border-r-0
          active:translate-y-1
          overflow-hidden
          group
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${className}
        `}
      >
        {/* Shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
        
        {/* Button content */}
        <div className="relative z-10 flex items-center justify-center gap-3">
          {icon && <span className="text-xl">{icon}</span>}
          {children}
        </div>
      </button>
    );
  };

  // Category Card Component
  const CategoryCard = ({ 
    category, 
    isSelected, 
    onClick 
  }: {
    category: string;
    isSelected: boolean;
    onClick: () => void;
  }) => {
    const categoryIcons = {
      'all': '📚',
      'faith': '🕌',
      'quran': '📖',
      'prophet': '🌙',
      'reflection': '🌟'
    };

    const categoryDescriptions = {
      'all': 'All Categories',
      'faith': 'Faith & Practice',
      'quran': 'Quran Knowledge',
      'prophet': 'Prophet Muhammad ﷺ',
      'reflection': 'Reflection Questions'
    };

    return (
      <button
        onClick={onClick}
        className={`
          relative
          p-6 rounded-2xl
          transition-all duration-300 ease-out
          transform
          hover:scale-105
          active:scale-95
          border-2
          shadow-lg
          hover:shadow-xl
          group
          text-left
          ${isSelected 
            ? `bg-gradient-to-br ${getCategoryColor(category)} text-white border-transparent shadow-xl scale-105` 
            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-300 dark:hover:border-blue-600'
          }
        `}
      >
        <div className="space-y-3">
          <div className={`text-3xl transition-transform duration-300 group-hover:scale-110 ${isSelected ? 'text-white' : 'text-gray-600'}`}>
            {categoryIcons[category as keyof typeof categoryIcons]}
          </div>
          <div>
            <div className={`font-bold text-lg mb-1 ${isSelected ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
              {categoryDescriptions[category as keyof typeof categoryDescriptions].split(' ')[0]}
            </div>
            <div className={`text-sm ${isSelected ? 'text-blue-100' : 'text-gray-600 dark:text-gray-400'}`}>
              {categoryDescriptions[category as keyof typeof categoryDescriptions].split(' ').slice(1).join(' ')}
            </div>
          </div>
        </div>

        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute top-3 right-3 w-3 h-3 bg-white rounded-full shadow-lg" />
        )}
      </button>
    );
  };

  if (!quizStarted) {
    return (
      <section className="section-padding bg-gradient-to-br from-blue-50 via-purple-50 to-cyan-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
        <div className="container">
          <div className="section-title text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 dark:text-white mb-4">📚 Islamic Knowledge Quiz</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Test your knowledge about Islam with this interactive quiz
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-200 dark:border-gray-700">
              <div className="text-center space-y-10">
                {/* Main Icon */}
                <div className="w-28 h-28 mx-auto bg-gradient-to-r from-emerald-400 to-green-500 rounded-2xl flex items-center justify-center text-white text-5xl shadow-2xl transform rotate-3 hover:rotate-0 transition-all duration-500">
                  📖
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-4xl font-bold text-gray-800 dark:text-white">
                    Ready to Test Your Knowledge?
                  </h3>
                  
                  <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-2xl mx-auto">
                    {score > 0 
                      ? `Masha'Allah! You scored ${score} out of ${filteredQuestions.length} in your last attempt!`
                      : "Challenge yourself with questions about Quran, Prophet Muhammad ﷺ, Islamic beliefs, and more."
                    }
                  </p>
                </div>

                {/* Category Selection */}
                <div className="space-y-6">
                  <label className="block text-2xl font-bold text-gray-800 dark:text-white text-center">
                    Choose Quiz Category:
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    {['all', 'faith', 'quran', 'prophet', 'reflection'].map((category) => (
                      <CategoryCard
                        key={category}
                        category={category}
                        isSelected={selectedCategory === category}
                        onClick={() => setSelectedCategory(category)}
                      />
                    ))}
                  </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                  <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-2xl border-2 border-blue-200 dark:border-blue-800 transform transition-all duration-300 hover:scale-105">
                    <div className="font-bold text-4xl text-blue-600 dark:text-blue-400 mb-2">{filteredQuestions.length}</div>
                    <div className="text-blue-700 dark:text-blue-300 font-semibold text-lg">Questions</div>
                  </div>
                  <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl border-2 border-purple-200 dark:border-purple-800 transform transition-all duration-300 hover:scale-105">
                    <div className="font-bold text-4xl text-purple-600 dark:text-purple-400 mb-2">4</div>
                    <div className="text-purple-700 dark:text-purple-300 font-semibold text-lg">Categories</div>
                  </div>
                </div>

                {/* Start Quiz Button */}
                <div className="pt-6">
                  <ImanButton 
                    onClick={startQuiz} 
                    variant="success" 
                    size="lg"
                    className="w-full max-w-md mx-auto text-xl"
                    icon="🚀"
                  >
                    {score > 0 ? 'Try Again' : 'Start Quiz'}
                  </ImanButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section-padding bg-gradient-to-br from-blue-50 via-purple-50 to-cyan-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 min-h-screen">
      <div className="container">
        <div className="max-w-4xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">
              <span>Question {currentQuestion + 1} of {filteredQuestions.length}</span>
              <div className="bg-gradient-to-r from-emerald-500 to-green-600 text-white px-4 py-1 rounded-full shadow-lg">
                Score: <span className="font-bold">{score}</span>
              </div>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 shadow-inner overflow-hidden">
              <div 
                className="bg-gradient-to-r from-emerald-500 to-green-600 h-3 rounded-full transition-all duration-500 shadow-lg"
                style={{ width: `${((currentQuestion + 1) / filteredQuestions.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Question Card */}
          <div className={`bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 md:p-10 border border-gray-200 dark:border-gray-700 transition-all duration-500 ${
            isAnimating ? 'opacity-0 transform translate-y-4' : 'opacity-100 transform translate-y-0'
          }`}>
            <div className="space-y-8">
              {/* Question Header */}
              <div className="flex items-center justify-between mb-6">
                <span className={`text-sm font-semibold px-4 py-2 rounded-full bg-gradient-to-r ${getCategoryColor(filteredQuestions[currentQuestion].category)} text-white shadow-lg`}>
                  {getCategoryName(filteredQuestions[currentQuestion].category)}
                </span>
                <div className="text-sm text-gray-500 dark:text-gray-400 font-medium bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                  Question {currentQuestion + 1}
                </div>
              </div>

              {/* Question */}
              <div className="text-center space-y-8">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white leading-relaxed">
                  "{filteredQuestions[currentQuestion].question}"
                </h3>

                {/* Answer Section */}
                {!showAnswer ? (
                  <div className="space-y-8 pt-8">
                    <p className="text-lg text-gray-600 dark:text-gray-400 italic">
                      Reflect on the answer, then reveal to check your understanding
                    </p>
                    <ImanButton 
                      onClick={revealAnswer} 
                      variant="primary" 
                      size="lg"
                      className="w-full max-w-md mx-auto text-xl"
                      icon="👇"
                    >
                      Reveal Answer
                    </ImanButton>
                  </div>
                ) : (
                  <div className="space-y-8 pt-8">
                    {/* Answer Display */}
                    <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 border-2 border-emerald-200 dark:border-emerald-800 rounded-2xl p-8 shadow-lg">
                      <h4 className="font-bold text-emerald-700 dark:text-emerald-300 mb-4 flex items-center justify-center gap-3 text-xl">
                        <span className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white shadow-lg">✓</span>
                        Correct Answer
                      </h4>
                      <p className="text-emerald-800 dark:text-emerald-200 whitespace-pre-line leading-relaxed text-lg text-center">
                        {filteredQuestions[currentQuestion].answer}
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                      <ImanButton 
                        onClick={markCorrect} 
                        variant="success" 
                        size="lg"
                        className="w-full text-lg"
                        icon="✅"
                      >
                        I Knew It!
                      </ImanButton>
                      <ImanButton 
                        onClick={nextQuestion} 
                        variant="accent" 
                        size="lg"
                        className="w-full text-lg"
                        icon="→"
                      >
                        Next Question
                      </ImanButton>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quiz Navigation */}
          <div className="flex justify-between items-center mt-8">
            <ImanButton 
              onClick={() => setQuizStarted(false)}
              variant="outline"
              size="md"
              className="text-lg"
              icon="←"
            >
              Exit Quiz
            </ImanButton>
            <div className="text-sm text-gray-500 dark:text-gray-400 font-medium bg-white dark:bg-gray-800 px-4 py-2 rounded-full border border-gray-200 dark:border-gray-700 shadow-lg">
              Progress: {currentQuestion + 1} / {filteredQuestions.length}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuizGame;